/**
 * Estilos inyectados de la pantalla de PreNómina.
 *
 * Patrón del repo: un const exportado que se renderiza como <style>{ESTILOS}</style>
 * (igual que Pages/Panel/Inicio/Inicio.js y Calidad/ReporteCalidad/reporteCalidadStyles.js).
 * Así el keyframe vive junto a la pantalla que lo usa y no engorda el CSS global.
 *
 * Prefijo `pn` a propósito: ya existe un `fadeUp` global en Inicio.js, más `calFadeUp`,
 * `repFadeUp` y `evpFadeUp` en otros módulos. Un nombre repetido se sobreescribiría en
 * silencio, porque los keyframes de CSS son globales aunque el <style> sea local.
 *
 * La curva cubic-bezier(0.22,1,0.36,1) es la que ya usan esos cuatro módulos.
 */
export const ESTILOS = `
  @keyframes pnFadeUp {
    from { opacity: 0; transform: translateY(26px) scale(0.985); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .pn-paso {
    animation: pnFadeUp 0.6s cubic-bezier(0.22,1,0.36,1) both;
  }

  /* ── Cortinilla de entrada a la nómina ──────────────────────────────────────
     Versión mínima de la intro del wizard de Configuración de Nómina
     (ConfiguracionInicialWizard.css): mismo lenguaje —fondo oscuro de marca,
     anillo cian, logo que entra girando— pero sin las cuatro ondas, las chispas,
     la tipografía gigante ni el botón de omitir.
     La del wizard dura ~4s porque se ve UNA vez en la vida de la empresa; ésta se
     cruza cada vez que se abre una nómina, así que vive menos de un segundo. */
  .pn-entrada {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background: linear-gradient(135deg, #001E26 0%, #003040 55%, #001A22 100%);
    animation: pnEntradaIn 0.22s ease-out both;
  }

  .pn-entrada-sale { animation: pnEntradaOut 0.45s ease-out forwards; }

  @keyframes pnEntradaIn  { from { opacity: 0; } to { opacity: 1; } }
  @keyframes pnEntradaOut { from { opacity: 1; } to { opacity: 0; } }

  /* Un solo anillo, en bucle: mientras la cortinilla siga puesta es lo que dice
     que algo está pasando. El wizard usa cuatro escalonados, que para una espera
     de medio segundo serían ruido. */
  .pn-entrada-anillo {
    position: absolute;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    border: 1.5px solid rgba(0, 191, 214, 0.45);
    animation: pnAnilloExpande 1.6s 0.1s ease-out infinite;
  }

  @keyframes pnAnilloExpande {
    0%   { transform: scale(0.35); opacity: 0; }
    25%  { opacity: 1; }
    100% { transform: scale(2.2); opacity: 0; }
  }

  /* inline-flex, no el display inline por defecto del span: transform y filter no
     aplican a un elemento inline no reemplazado, y la animación no se vería. */
  .pn-entrada-logo {
    position: relative;
    display: inline-flex;
    animation: pnLogoEntra 0.8s cubic-bezier(0.34, 1.25, 0.64, 1) both;
  }

  @keyframes pnLogoEntra {
    0%   { opacity: 0; transform: scale(0.5) rotateY(-140deg); filter: drop-shadow(0 0 0 transparent); }
    40%  { opacity: 1; }
    70%  { transform: scale(1.08) rotateY(8deg); filter: drop-shadow(0 0 42px rgba(0, 191, 214, 0.95)); }
    100% { opacity: 1; transform: scale(1) rotateY(0deg); filter: drop-shadow(0 0 26px rgba(0, 191, 214, 0.55)); }
  }

  /* Respetar a quien pidió menos movimiento en su sistema. Mismo guard que App.css
     usa para el logo del login, hoy el único lugar del repo que lo honra.
     La cortinilla no desaparece —sigue tapando la transición— pero se queda quieta. */
  @media (prefers-reduced-motion: reduce) {
    .pn-paso { animation: none; }
    .pn-entrada-anillo { animation: none; opacity: 0.5; }
    .pn-entrada-logo { animation: none; }
  }
`

export default ESTILOS
